// src/pages/HomePage.tsx

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useGetWorkspacesQuery, useGetWorkspaceCreditsQuery } from '../api/workspacesApi';
import { useGetBillingOverviewQuery } from '../api/billingApi';
import { useCreateConversationMutation, useAddMessageMutation } from '../api/conversationsApi';
import {
  useGetManagementSummaryQuery,
  useGetTeamRebalancingRecommendationsQuery,
} from '../api/managementApi';
import { useGetGoalsQuery } from '../api/goalsApi';
import { useGetTasksQuery } from '../api/tasksApi';
import { deriveInterventions } from '../services/interventionEngine';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import UpcomingScheduleCard from '../components/home/UpcomingScheduleCard';
import ExecutionCenter from '../components/home/ExecutionCenter';
import PerformanceCard from '../components/home/PerformanceCard';
import { useHasCapability } from '../services/centralContext';
import { useAuth } from '../hooks/useAuth';

// ============================================================
// Trend helpers
// ============================================================

interface WeekRange { start: string; end: string }

function getTrendWeeks(): { thisWeek: WeekRange; lastWeek: WeekRange } {
  const now = new Date();
  const diffToMon = (now.getDay() + 6) % 7;
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - diffToMon);
  thisMonday.setHours(0, 0, 0, 0);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);
  const nextMonday = new Date(thisMonday);
  nextMonday.setDate(thisMonday.getDate() + 7);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return {
    thisWeek: { start: fmt(thisMonday), end: fmt(nextMonday) },
    lastWeek: { start: fmt(lastMonday), end: fmt(thisMonday) },
  };
}

const TREND_WEEKS = getTrendWeeks();
type TrendDir = 'up' | 'down' | 'flat';

function calcTrend(current: number, prev: number): TrendDir {
  if (current > prev) return 'up';
  if (current < prev) return 'down';
  return 'flat';
}

// ============================================================

export default function HomePage() {
  const navigate = useNavigate();
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);

  const { isLoading: isLoadingWorkspaces, error: workspacesError } = useGetWorkspacesQuery();
  const activeWorkspace = useActiveWorkspace();
  const { user } = useAuth();

  const { data: managementSummary } = useGetManagementSummaryQuery(undefined, {
    skip: !activeWorkspace,
  });

  const today = new Date().toISOString().split('T')[0];

  const { data: execGoalsData } = useGetGoalsQuery(
    { workspaceId: activeWorkspace?.workspace.id, status: 'active', limit: 20 },
    { skip: !activeWorkspace }
  );

  const { data: rebalancing } = useGetTeamRebalancingRecommendationsQuery(undefined, {
    skip: !activeWorkspace,
  });

  const { data: tasksData } = useGetTasksQuery(
    { workspaceId: activeWorkspace?.workspace.id },
    { skip: !activeWorkspace }
  );

  const {
    data: credits,
    isLoading: isLoadingCredits,
  } = useGetWorkspaceCreditsQuery(activeWorkspace?.workspace.id || '', {
    skip: !activeWorkspace,
  });

  // Central-derived billing overview — used for credits display (AI gating via Central capabilities below)
  const { data: billing } = useGetBillingOverviewQuery(undefined, {
    skip: !activeWorkspace,
  });

  // Central context entitlement check — capability-first resolution
  // Central capabilities are the SOLE authority for AI feature gating.
  const hasAICapability = useHasCapability('ai', 'write');
  // Use Central capability check ONLY — no billing API fallback.
  const allowAI = hasAICapability;

  const [createConversation] = useCreateConversationMutation();
  const [addMessage] = useAddMessageMutation();
  const [isCreatingExecAI, setIsCreatingExecAI] = useState(false);

  const execScorecard = useMemo(() => {
    const goals = execGoalsData?.goals ?? [];
    const totalActive = goals.length;
    const behindGoals = goals.filter((g) => g.progress.trackingStatus === 'behind');
    const behindCount = behindGoals.length;
    const onTrackCount = goals.filter((g) =>
      g.progress.trackingStatus === 'on_track' || g.progress.trackingStatus === 'ahead'
    ).length;
    const overdueCount = managementSummary?.gaps.overdueTasks ?? 0;
    const blockedCount = managementSummary?.gaps.blockedTasks ?? 0;
    const capacity = managementSummary?.capacity ?? [];
    const overloadedCount = capacity.filter(
      (m) => m.openTaskCount > 10 || m.estimatedMinutes > 480
    ).length;
    const rebalancingCount = rebalancing?.recommendations.length ?? 0;

    // Week-over-week trends derived from task timestamps
    const tasks = tasksData?.tasks ?? [];
    const completedThisWeek = tasks.filter((t) => {
      if (t.status !== 'done') return false;
      const ts = (t.completedAt ?? t.updatedAt).split('T')[0];
      return ts >= TREND_WEEKS.thisWeek.start && ts < TREND_WEEKS.thisWeek.end;
    }).length;
    const completedLastWeek = tasks.filter((t) => {
      if (t.status !== 'done') return false;
      const ts = (t.completedAt ?? t.updatedAt).split('T')[0];
      return ts >= TREND_WEEKS.lastWeek.start && ts < TREND_WEEKS.thisWeek.start;
    }).length;
    // Approximation: tasks overdue at end of last week = due before this week and not yet completed by last week end
    const overdueLastWeek = tasks.filter((t) => {
      if (!t.dueDate) return false;
      if (t.dueDate.split('T')[0] >= TREND_WEEKS.thisWeek.start) return false;
      const completedDate = t.completedAt ? t.completedAt.split('T')[0] : null;
      return !completedDate || completedDate >= TREND_WEEKS.thisWeek.start;
    }).length;

    const overdueTrend = calcTrend(overdueCount, overdueLastWeek);
    const completedTrend = calcTrend(completedThisWeek, completedLastWeek);

    // Predictive: 48h at-risk tasks (computed once, used in both attention + return)
    const todayStr = new Date().toISOString().split('T')[0];
    const in48hStr = (() => { const d = new Date(); d.setHours(d.getHours() + 48); return d.toISOString().split('T')[0]; })();
    const atRisk48h = tasks.filter((t) => {
      if (!t.dueDate) return false;
      const d = t.dueDate.split('T')[0];
      return d >= todayStr && d <= in48hStr && (t.status === 'todo' || t.status === 'blocked');
    }).length;

    // Trend-aware + predictive attention items
    // Each item carries a `key` for deduplication against higher-tier interventions.
    const attention: { label: string; sev: 'high' | 'med' | 'low'; key: string }[] = [];

    // Goal risk — behind goals get predictive language when execution signals are present
    behindGoals.slice(0, 2).forEach((g) => {
      const exec = g.executionSummary;
      const badPace = g.progress.paceGap != null && g.progress.paceGap < -10;
      if (exec && (exec.overdueTasks > 0 || exec.blockedTasks > 0 || badPace)) {
        const reasons: string[] = [];
        if (badPace) reasons.push(`${Math.abs(g.progress.paceGap!).toFixed(0)}% behind pace`);
        if (exec.overdueTasks > 0) reasons.push(`${exec.overdueTasks} overdue task${exec.overdueTasks > 1 ? 's' : ''}`);
        if (exec.blockedTasks > 0) reasons.push(`${exec.blockedTasks} blocked`);
        attention.push({ label: `Risk: "${g.title}" — ${reasons.join(', ')} — likely to miss period`, sev: 'high', key: `goal:${g.id}` });
      } else {
        attention.push({ label: `Goal behind: ${g.title}`, sev: 'high', key: `goal:${g.id}` });
      }
    });

    // Overdue trend — kept even when interventions exist (trend context is additive, not redundant)
    if (overdueCount > 0) {
      if (overdueTrend === 'up')
        attention.push({ label: `Overdue tasks increased from ${overdueLastWeek} → ${overdueCount}`, sev: 'high', key: 'overdue-trend' });
      else if (overdueTrend === 'flat')
        attention.push({ label: `${overdueCount} overdue task${overdueCount > 1 ? 's' : ''} — unchanged`, sev: 'med', key: 'overdue-trend' });
      else
        attention.push({ label: `Overdue tasks improving (${overdueLastWeek} → ${overdueCount})`, sev: 'low', key: 'overdue-trend' });
    }

    // Predictive: tasks due within 48h — suppressed if an intervention already targets this
    if (atRisk48h > 0)
      attention.push({ label: `${atRisk48h} task${atRisk48h > 1 ? 's' : ''} due within 48h — not started or blocked`, sev: 'med', key: '48h' });

    if (overloadedCount > 0)
      attention.push({ label: `${overloadedCount} team member${overloadedCount > 1 ? 's' : ''} overloaded`, sev: 'med', key: 'overload' });
    if (blockedCount > 0)
      attention.push({ label: `${blockedCount} task${blockedCount > 1 ? 's' : ''} blocked — unchanged`, sev: 'med', key: 'blocked' });
    if (rebalancingCount > 0)
      attention.push({ label: `${rebalancingCount} rebalancing suggestion${rebalancingCount > 1 ? 's' : ''} pending`, sev: 'low', key: 'rebalancing' });

    return {
      totalActive, behindCount, onTrackCount,
      overdueCount, blockedCount,
      overloadedCount, rebalancingCount,
      completedThisWeek, completedLastWeek, completedTrend,
      overdueLastWeek, overdueTrend,
      // Full list — slicing happens after deduplication in the component
      attentionItems: attention,
      behindGoals,
    };
  }, [execGoalsData, managementSummary, rebalancing, tasksData]);

  const interventions = useMemo(
    () =>
      deriveInterventions({
        goals: execGoalsData?.goals ?? [],
        tasks: tasksData?.tasks ?? [],
        mgmt: managementSummary,
        rebalancing,
        weekStart: TREND_WEEKS.thisWeek.start,
        weekEnd: TREND_WEEKS.thisWeek.end,
        maxCount: 5,
      }),
    [execGoalsData, tasksData, managementSummary, rebalancing],
  );

  const handleExecAI = async () => {
    if (!activeWorkspace) return;
    setIsCreatingExecAI(true);
    try {
      const result = await createConversation({
        workspaceId: activeWorkspace.workspace.id,
        title: 'Workspace health summary',
        mode: 'chat',
      }).unwrap();
      const sessionId = result.session.id;
      const { totalActive, behindCount, onTrackCount, overdueCount, blockedCount, overloadedCount, rebalancingCount, attentionItems, behindGoals } = execScorecard;
      const lines: string[] = [];
      lines.push(`**Workspace Health Summary — ${activeWorkspace.workspace.name}**`);
      lines.push('');
      lines.push('**Goal Health**');
      lines.push(`- ${totalActive} active goal${totalActive !== 1 ? 's' : ''}`);
      if (onTrackCount > 0) lines.push(`- ${onTrackCount} on track or ahead`);
      if (behindCount > 0) {
        lines.push(`- ${behindCount} behind`);
        behindGoals.slice(0, 3).forEach((g) => {
          const pace = g.progress.paceGap != null
            ? ` (${g.progress.paceGap > 0 ? '+' : ''}${g.progress.paceGap.toFixed(1)}% vs pace)`
            : '';
          lines.push(`  - ${g.title}${pace}`);
        });
      }
      lines.push('');
      lines.push('**Execution Health**');
      if (overdueCount > 0) lines.push(`- ${overdueCount} task${overdueCount !== 1 ? 's' : ''} overdue`);
      if (blockedCount > 0) lines.push(`- ${blockedCount} task${blockedCount !== 1 ? 's' : ''} blocked`);
      if (overdueCount === 0 && blockedCount === 0) lines.push('- No overdue or blocked tasks');
      lines.push('');
      lines.push('**Team Health**');
      if (overloadedCount > 0) lines.push(`- ${overloadedCount} team member${overloadedCount !== 1 ? 's' : ''} overloaded`);
      else lines.push('- No team members overloaded');
      if (rebalancingCount > 0) lines.push(`- ${rebalancingCount} rebalancing suggestion${rebalancingCount !== 1 ? 's' : ''} pending`);
      if (attentionItems.length > 0) {
        lines.push('');
        lines.push('**Top Attention Items**');
        attentionItems.forEach((item) => lines.push(`- ${item.label}`));
      }
      lines.push('');
      lines.push('Please summarize the current health of this workspace. What are the top 3 things for leadership to address this week? Keep the response concise and actionable.');
      try {
        await addMessage({ sessionId, content: lines.join('\n') }).unwrap();
      } catch { /* primer send failed — still navigate */ }
      navigate(`/conversations/${sessionId}?assistant=true`);
    } catch (e) {
      console.error('Failed to create exec AI conversation:', e);
    } finally {
      setIsCreatingExecAI(false);
    }
  };

  const handleTalkToLevelUp = async () => {
    if (!activeWorkspace) return;

    setIsCreatingConversation(true);
    try {
      const result = await createConversation({
        workspaceId: activeWorkspace.workspace.id,
        title: 'Talk to LevelUp',
        mode: 'chat',
      }).unwrap();

      // Navigate to the conversation with assistant mode enabled
      navigate(`/conversations/${result.session.id}?assistant=true`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setIsCreatingConversation(false);
    }
  };

  if (isLoadingWorkspaces) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (workspacesError) {
    return <ErrorState message="Failed to load workspace data." />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Workspace</h2>
        <p className="text-gray-500">Please select or create a workspace to get started.</p>
      </div>
    );
  }

  const workspace = activeWorkspace.workspace;

  return (
    <div className="space-y-6">
      {/* Demo Workspace Banner */}
      {workspace.isDemo && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Demo Workspace</p>
            <p className="text-sm text-amber-700">
              This is a sample demo workspace. Data here is for exploration only and will not affect your real workspaces.
            </p>
          </div>
        </div>
      )}

      {/* Welcome Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Welcome to {workspace.name}
        </h1>
        <p className="text-gray-500 capitalize">
          {workspace.type} workspace
          {billing?.planName && ` · ${billing.planName} plan`}
        </p>
        {billing?.planStatus === 'trialing' && billing.trialEndsAt && (
          <div className="mt-3 inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
            Trial ends {new Date(billing.trialEndsAt).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Execution Center — deterministic execution intelligence.
          Supersedes the standalone Today's Focus, Overdue/Due Today, and
          Suggested Priorities cards (those decisions now live here). */}
      <ExecutionCenter
        workspaceId={workspace.id}
        forUserId={user?.id ?? null}
        baseInterventions={interventions}
      />

      {/* Upcoming Schedule — detailed event list (Execution Center only uses
          events for capacity, so this remains unique). */}
      <UpcomingScheduleCard workspaceId={workspace.id} today={today} />

      {/* Performance — consolidated "are we winning or falling behind?" */}
      <PerformanceCard
        workspaceId={workspace.id}
        today={today}
        onAskAI={handleExecAI}
        isAskingAI={isCreatingExecAI}
        allowAI={allowAI}
      />

      {/* ── Lower-priority utilities (below the deterministic operating surfaces) ── */}

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/conversations')}
            className="p-4 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="text-2xl mb-2 block">💬</span>
            <span className="font-medium text-gray-900">Conversations</span>
          </button>
          <button
            onClick={() => navigate('/contacts')}
            className="p-4 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="text-2xl mb-2 block">👥</span>
            <span className="font-medium text-gray-900">View Contacts</span>
          </button>
          <button
            onClick={() => navigate('/tasks')}
            className="p-4 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="text-2xl mb-2 block">📋</span>
            <span className="font-medium text-gray-900">Manage Tasks</span>
          </button>
          <button
            onClick={() => navigate('/schedule')}
            className="p-4 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="text-2xl mb-2 block">📅</span>
            <span className="font-medium text-gray-900">Events</span>
          </button>
          <button
            onClick={() => navigate('/plan')}
            className="p-4 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="text-2xl mb-2 block">🗓️</span>
            <span className="font-medium text-gray-900">Weekly Plan</span>
          </button>
        </div>
      </div>

      {/* Talk to LevelUp — de-emphasized AI assistant entry */}
      <button
        onClick={handleTalkToLevelUp}
        disabled={isCreatingConversation}
        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors disabled:opacity-60"
      >
        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        {isCreatingConversation ? 'Starting conversation…' : 'Talk to LevelUp'}
      </button>

      {/* Credits Section — Central entitlement check via scopes */}
      {allowAI && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Credit Wallets</h2>
          {isLoadingCredits ? (
            <LoadingSpinner />
          ) : credits?.wallets.length ? (
            <div className="space-y-3">
              {credits.wallets.map((wallet) => (
                <div
                  key={wallet.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <span className="font-medium text-gray-700">{wallet.name}</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {wallet.balance.toLocaleString()} credits
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No credit wallets configured.</p>
          )}
        </div>
      )}
    </div>
  );
}
